import { useMemo, useState } from 'react';
import { useSuspenseFeatureGate } from 'FrontRoyalStatsig';
import {
    type WeeklyVoiceLimitState,
    useWeeklyVoiceTimeLimitState,
    UI_CONTEXT_REVIEW_PREVIOUS_MATERIAL,
    UI_CONTEXT_REVIEW_LESSON,
    UI_CONTEXT_REVIEW_PREVIOUS_MATERIAL_AUDIO,
    UI_CONTEXT_REVIEW_LESSON_AUDIO,
    type LessonPlayerBotAudioUiContext,
} from 'TutorBotConversation';

import { type LessonPlayerBotChatUiContext, type LessonPlayerBotUiContext } from '../TutorBotAiTutorChat.types';

const textToAudioUiContextMap = {
    [UI_CONTEXT_REVIEW_PREVIOUS_MATERIAL]: UI_CONTEXT_REVIEW_PREVIOUS_MATERIAL_AUDIO,
    [UI_CONTEXT_REVIEW_LESSON]: UI_CONTEXT_REVIEW_LESSON_AUDIO,
} as const;
type ToggleableTextUiContext = keyof typeof textToAudioUiContextMap;
type AudioUiContext = (typeof textToAudioUiContextMap)[ToggleableTextUiContext];

type OverTimeLimitConfig = {
    showDisabledVoiceChatButtonReason: 'overWeeklyVoiceTimeLimit';
    weeklyVoiceTimeLimitState: WeeklyVoiceLimitState;
    canLaunchReviewPreviousMaterialAudio: false;
    toggleAudioOrText: undefined;
};

type CanToggleAudioOrTextConfig = {
    toggleAudioOrText: () => void;
    canLaunchReviewPreviousMaterialAudio: true;
    weeklyVoiceTimeLimitState: undefined;
};

type Config = OverTimeLimitConfig | CanToggleAudioOrTextConfig | null;

function getOverTimeLimitConfig(weeklyVoiceTimeLimitState: WeeklyVoiceLimitState): OverTimeLimitConfig {
    return {
        showDisabledVoiceChatButtonReason: 'overWeeklyVoiceTimeLimit',
        weeklyVoiceTimeLimitState,
        canLaunchReviewPreviousMaterialAudio: false,
        toggleAudioOrText: undefined,
    };
}

function getCanToggleAudioOrTextConfig({
    uiContext,
    setAutoStartVoiceConversation,
    setUiContext,
}: {
    uiContext: LessonPlayerBotUiContext;
    setAutoStartVoiceConversation: (autoStartVoiceConversation: boolean) => void;
    setUiContext: (uiContext: LessonPlayerBotUiContext) => void;
}): CanToggleAudioOrTextConfig {
    return {
        toggleAudioOrText: () => {
            if (isToggleableTextUiContext(uiContext)) {
                // If the user clicks to toggle from text-mode to voice-mode, we want to
                // skip the start screen and just start the conversation
                setAutoStartVoiceConversation(true);
                setUiContext(textToAudioUiContextMap[uiContext]);
            } else if (isAudioUiContext(uiContext)) {
                setUiContext(getTextUiContext(uiContext));
            } else {
                throw new Error('Unsupported uiContext');
            }
        },
        canLaunchReviewPreviousMaterialAudio: true,
        weeklyVoiceTimeLimitState: undefined,
    };
}

function isToggleableTextUiContext(uiContext: LessonPlayerBotUiContext): uiContext is ToggleableTextUiContext {
    return Object.keys(textToAudioUiContextMap).includes(uiContext);
}

function isAudioUiContext(uiContext: LessonPlayerBotUiContext): uiContext is AudioUiContext {
    return Object.values(textToAudioUiContextMap).includes(uiContext as LessonPlayerBotAudioUiContext);
}

function getTextUiContext(audioUiContext: AudioUiContext): ToggleableTextUiContext {
    const key = Object.entries(textToAudioUiContextMap).find(([_, v]) => v === audioUiContext)?.[0];
    if (!key) throw new Error('Invalid audioUiContext');
    return key as ToggleableTextUiContext;
}

function supportsTextAudioToggle(uiContext: LessonPlayerBotUiContext): boolean {
    if (isToggleableTextUiContext(uiContext)) return true;
    if (isAudioUiContext(uiContext)) return true;
    return false;
}

function getInitialUiContext({
    canLaunchReviewPreviousMaterialAudio,
    providedUiContext,
}: {
    canLaunchReviewPreviousMaterialAudio: boolean;
    providedUiContext: LessonPlayerBotChatUiContext;
}): LessonPlayerBotUiContext {
    if (!canLaunchReviewPreviousMaterialAudio) return providedUiContext;

    if (isToggleableTextUiContext(providedUiContext)) {
        return textToAudioUiContextMap[providedUiContext];
    }

    return providedUiContext;
}

export function useToggleAudioOrText({ providedUiContext }: { providedUiContext: LessonPlayerBotChatUiContext }): {
    toggleAudioOrTextConfig: Config;
    autoStartVoiceConversation: boolean;
    uiContext: LessonPlayerBotUiContext;
} {
    const [autoStartVoiceConversation, setAutoStartVoiceConversation] = useState(false);
    const supportsReviewPreviousMaterialAudio = useSuspenseFeatureGate('audio_recap_lesson');
    const weeklyVoiceTimeLimitState = useWeeklyVoiceTimeLimitState();
    const canLaunchReviewPreviousMaterialAudio =
        supportsReviewPreviousMaterialAudio && !weeklyVoiceTimeLimitState.overTimeLimit;

    // The UiContext that's passed in is always one of the Chat UiContexts. When it's `review_previous_material`,
    // we switch over to the `_audio` ui context.
    const [uiContext, setUiContext] = useState<LessonPlayerBotUiContext>(() =>
        getInitialUiContext({ canLaunchReviewPreviousMaterialAudio, providedUiContext }),
    );

    const config = useMemo(() => {
        // If the ui context does not support audio, we don't need a config here
        if (!supportsTextAudioToggle(uiContext)) return null;

        // If voice mode is not supported, we don't need a config here
        if (!supportsReviewPreviousMaterialAudio) return null;

        if (weeklyVoiceTimeLimitState.overTimeLimit) {
            return getOverTimeLimitConfig(weeklyVoiceTimeLimitState);
        }

        if (canLaunchReviewPreviousMaterialAudio) {
            return getCanToggleAudioOrTextConfig({ uiContext, setAutoStartVoiceConversation, setUiContext });
        }

        throw new Error('Invalid situation');
    }, [
        setAutoStartVoiceConversation,
        setUiContext,
        supportsReviewPreviousMaterialAudio,
        uiContext,
        weeklyVoiceTimeLimitState,
        canLaunchReviewPreviousMaterialAudio,
    ]);

    return {
        uiContext,
        toggleAudioOrTextConfig: config,
        autoStartVoiceConversation,
    };
}
